import React from "react";
import classNames from "classnames";
import { format, parseISO } from "date-fns";
import { fullDateLongFormat } from "@common/helpers/date";
import { pluralizeWithCount } from "@common/helpers/string";
import { SchedulesReportRowKind } from "@common/types/reports/schedules-report";
import { CellContext } from "@tanstack/react-table";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import {
    scheduleReportTitleTarget,
    SchedulesReportTableMeta,
} from "./schedulesReportDrillDown.lib";
import { SchedulesReportViewRow } from "./schedulesReportViewTypes";
import styles from "./SchedulesReportTable.module.scss";

export const SchedulesReportTitleCell: React.FC<
    CellContext<ViewTableFeatures, SchedulesReportViewRow, unknown>
> = React.memo(({ row, table }) => {
    const meta = table.options.meta as SchedulesReportTableMeta | undefined;
    const onOpenSchedule = meta?.callbacks?.onOpenSchedule;
    const {
        rowKind,
        templateId,
        title,
        counterpartyName,
        scheduleApplicationId,
    } = row.original;
    const target = scheduleReportTitleTarget({
        rowKind,
        counterpartyName,
        templateId,
        scheduleApplicationId,
    });
    const isStartPeriod = rowKind === SchedulesReportRowKind.startPeriod;
    const scheduleCount = row.original.drill?.scheduleApplicationIds.length;
    const isEmptyStartPeriod = isStartPeriod && !scheduleCount;
    const isLinkable =
        onOpenSchedule !== undefined && target !== null && !isEmptyStartPeriod;
    const tooltip =
        isStartPeriod && scheduleCount
            ? pluralizeWithCount("schedule", scheduleCount)
            : undefined;
    const titleClassName = classNames({
        [styles.startPeriodTitle]: isStartPeriod,
    });
    return (
        <ViewTableCell
            className={classNames({
                [styles.totalCell]: row.original.isTotal,
                [styles.analysisCell]: row.original.isAnalysis,
            })}
        >
            <Tooltip content={tooltip} align="start">
                {isLinkable ? (
                    <button
                        type="button"
                        className={classNames(
                            "data-table__drill-down",
                            styles.titleLinkButton,
                            titleClassName,
                        )}
                        onClick={() => onOpenSchedule(target)}
                    >
                        {title}
                    </button>
                ) : (
                    <span className={titleClassName}>{title}</span>
                )}
            </Tooltip>
        </ViewTableCell>
    );
});

SchedulesReportTitleCell.displayName = "SchedulesReportTitleCell";

function formatTermDate(value: string | null | undefined): string {
    return value ? format(parseISO(value), fullDateLongFormat) : "";
}

interface TermDateCellParams {
    field: "termStart" | "termEnd";
}

export function makeSchedulesReportTermDateCell({ field }: TermDateCellParams) {
    const Cell: React.FC<
        CellContext<ViewTableFeatures, SchedulesReportViewRow, unknown>
    > = React.memo(({ row }) => (
        <ViewTableCell>{formatTermDate(row.original[field])}</ViewTableCell>
    ));
    Cell.displayName = `SchedulesReportTermDateCell_${field}`;
    return Cell;
}
